import React, { useMemo } from "react";
import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PublicIcon from "@mui/icons-material/Public";
import WorkIcon from "@mui/icons-material/Work";
import { Transition } from "react-transition-group";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";

const statusChipStyles = {
  Joined: { bg: "#eaf6ee", color: "#1f7a45", border: "#b9dfc8" },
  Requested: { bg: "#fff8e8", color: "#9b6b00", border: "#ffe2aa" },
  Pending: { bg: "#fff8e8", color: "#9b6b00", border: "#ffe2aa" },
  Rejected: { bg: "#ffefef", color: "#c43838", border: "#f3caca" },
  Removed: { bg: "#ffefef", color: "#c43838", border: "#f3caca" },
  "Remove-Requested": { bg: "#ffefef", color: "#c43838", border: "#f3caca" },
  "Not-Requested": { bg: "#f1f5f9", color: "#475569", border: "#d0d7de" },
};

const InfoRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.8, textTransform: "uppercase", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#1e293b", fontWeight: 500, wordBreak: "break-word", lineHeight: 1.4 }}>
        {value}
      </Typography>
    </Box>
  );
};

const BlurTransition = React.forwardRef(function BlurTransition(props, ref) {
  const { in: inProp, children, onEnter, onExited, ...other } = props;

  const defaultStyle = {
    opacity: 0,
    filter: "blur(8px)",
    transform: "translateY(10px)",
    transition: "opacity 280ms ease, filter 280ms ease, transform 280ms ease",
  };

  const transitionStyles = {
    entering: { opacity: 1, filter: "blur(0px)", transform: "translateY(0)" },
    entered: { opacity: 1, filter: "blur(0px)", transform: "translateY(0)" },
    exiting: { opacity: 0, filter: "blur(8px)", transform: "translateY(10px)" },
    exited: { opacity: 0, filter: "blur(8px)", transform: "translateY(10px)" },
  };

  return (
    <Transition
      in={inProp}
      timeout={{ enter: 280, exit: 220 }}
      onEnter={onEnter}
      onExited={onExited}
      {...other}
    >
      {(state) =>
        React.cloneElement(children, {
          ref,
          style: {
            ...defaultStyle,
            ...transitionStyles[state],
            ...children.props.style,
          },
        })
      }
    </Transition>
  );
});

const UserDetailsModal = ({
  open,
  onClose,
  user,
  platformId,
  orgIdToName = {},
  costCenterById = {},
  isLoading = false,
  error = "",
}) => {
  const orgs = useMemo(() => {
    if (!user || !platformId) return [];
    const organizations = user?.platforms?.[platformId]?.organizations || {};
    return Object.entries(organizations)
      .filter(([, value]) => value?.membership_status === "Joined")
      .map(([id, value]) => ({ id, ...value }))
      .sort((a, b) => {
        const aDate = a?.status_history?.[a.status_history.length - 1]?.changed_date;
        const bDate = b?.status_history?.[b.status_history.length - 1]?.changed_date;
        return new Date(bDate || 0) - new Date(aDate || 0);
      });
  }, [user, platformId]);

  const costCenterId = user?.platforms?.[platformId]?.cost_center_id;
  const costCenterName = costCenterById[costCenterId] || costCenterId || "";
  const opData = user?.op_data || {};

  const fullName =
    user?.full_name ||
    [user?.last_name, user?.first_name].filter(Boolean).join(", ") ||
    "Unknown User";

  const hasOpProfile = Boolean(
    costCenterName ||
    opData.rc_leader_name ||
    opData.gps_indicator ||
    opData.crcc_business_name ||
    opData.crcc_business_area ||
    opData.crcc_business_line
  );

  const tagData = [
    { icon: <PublicIcon sx={{ fontSize: 13 }} />, value: user?.country, bg: "#eaf7fb", color: "#0c5f77" },
    { icon: <BusinessIcon sx={{ fontSize: 13 }} />, value: user?.department, bg: "#edf7ef", color: "#1f6a37" },
    { icon: <WorkIcon sx={{ fontSize: 13 }} />, value: user?.business_function, bg: "#fff6e8", color: "#9a4e07" },
  ].filter((t) => t.value);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={BlurTransition}
      transitionDuration={{ enter: 280, exit: 220 }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          maxHeight: "85vh",
          transition: "box-shadow 0.28s ease",
        },
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          background: "#0b0b0b",
          color: "#fff",
          borderBottom: "1px solid #1f2937",
          px: 3,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PersonOutlineIcon sx={{ fontSize: 22, color: "rgba(255,255,255,0.85)" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 18, letterSpacing: 0.3 }}>
            User Profile
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          size="small"
          onClick={onClose}
          sx={{ color: "#fff", "&:hover": { background: "rgba(255,255,255,0.12)" } }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, background: "#f8fafc" }}>
        {isLoading && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
            <svg width="48" height="48" viewBox="0 0 50 50">
              <circle cx="25" cy="25" r="20" fill="none" stroke="#0b756c" strokeWidth="5" strokeDasharray="31.4 31.4" strokeLinecap="round">
                <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
              </circle>
            </svg>
            <Typography sx={{ color: "#0b756c", fontWeight: 600 }}>Loading user details...</Typography>
          </Box>
        )}

        {!isLoading && error && (
          <Box sx={{ p: 4 }}>
            <Typography sx={{ color: "#d32f2f", fontWeight: 600 }}>{error}</Typography>
          </Box>
        )}

        {!isLoading && !error && user && (
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, minHeight: 400 }}>
            {/* ===== LEFT PANEL: Profile Card ===== */}
            <Box
              sx={{
                width: { xs: "100%", md: 340 },
                flexShrink: 0,
                background: "#fff",
                borderRight: { md: "1px solid #e0e7ef" },
                borderBottom: { xs: "1px solid #e0e7ef", md: "none" },
                p: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: 0,
              }}
            >
              {/* Avatar + Name */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #eef7f6 0%, #d9ecea 100%)",
                    color: "#0b756c",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 22,
                    border: "2px solid #d2e9e6",
                    flexShrink: 0,
                  }}
                >
                  {user?.first_name?.[0] || user?.last_name?.[0] || "U"}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#1e293b", lineHeight: 1.3 }}>
                    {fullName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "#64748b",
                      wordBreak: "break-all",
                      lineHeight: 1.3,
                    }}
                  >
                    {user?.email || "-"}
                  </Typography>
                </Box>
              </Box>

              {/* Tags: Country, Department, Business Function */}
              {tagData.length > 0 && (
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2 }}>
                  {tagData.map((tag, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        background: tag.bg,
                        color: tag.color,
                        borderRadius: 1.25,
                        px: 1,
                        py: 0.4,
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    >
                      {tag.icon}
                      {tag.value}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Cost & Billing Section */}
              {hasOpProfile && (
                <Box
                  sx={{
                    background: "#fcfdfd",
                    border: "1px solid #edf1f4",
                    borderRadius: 2.5,
                    p: 2,
                    mb: 1.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: "#0b756c", fontSize: 12, mb: 1.5, letterSpacing: 0.5 }}>
                    COST &amp; BILLING PROFILE
                  </Typography>

                  <InfoRow label="GitHub Cost Center" value={costCenterName} />
                  <InfoRow label="Business Name" value={opData.crcc_business_name} />

                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <InfoRow label="Business Area" value={opData.crcc_business_area} />
                    <InfoRow label="Business Line" value={opData.crcc_business_line} />
                  </Box>

                  {/* RC Leader & GPS badges */}
                  {(opData.rc_leader_name || opData.gps_indicator) && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                      {opData.rc_leader_name && (
                        <Box
                          sx={{
                            background: "#eef6ff",
                            color: "#0969da",
                            border: "1px solid #c6ddff",
                            borderRadius: 3,
                            px: 1.25,
                            py: 0.4,
                            fontSize: 11,
                            fontWeight: 500,
                          }}
                        >
                          RC Leader: {opData.rc_leader_name}
                        </Box>
                      )}
                      {opData.gps_indicator && (
                        <Box
                          sx={{
                            background: "#ecfdf3",
                            color: "#116329",
                            border: "1px solid #b4e2c8",
                            borderRadius: 3,
                            px: 1.25,
                            py: 0.4,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          GPS: {opData.gps_indicator}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* If no op_data but has cost center, still show it */}
              {!hasOpProfile && costCenterName && (
                <Box sx={{ mb: 1.5 }}>
                  <InfoRow label="GitHub Cost Center" value={costCenterName} />
                </Box>
              )}
            </Box>

            {/* ===== RIGHT PANEL: Organization Memberships ===== */}
            <Box sx={{ flex: 1, p: 2.5, display: "flex", flexDirection: "column", minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0b756c" }}>
                  Organization Memberships
                </Typography>
                {orgs.length > 0 && (
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                    <Box
                      sx={{
                        fontSize: 11,
                        px: 1.25,
                        py: 0.3,
                        borderRadius: 5,
                        background: "#f0f4f8",
                        fontWeight: 700,
                        color: "#334155",
                      }}
                    >
                      Joined: {orgs.length}
                    </Box>
                  </Box>
                )}
              </Box>

              {orgs.length === 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#94a3b8",
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                >
                  No joined organizations found for this user.
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid #edf1f4",
                    borderRadius: 2,
                    overflow: "hidden",
                    maxHeight: 420,
                    overflowY: "auto",
                    background: "#fff",
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f8fafc", position: "sticky", top: 0, zIndex: 1 }}>
                        <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 12, fontWeight: 700, color: "#0b756c", borderBottom: "2px solid #e0e7ef" }}>
                          Organization
                        </th>
                        <th style={{ textAlign: "center", padding: "10px 14px", fontSize: 12, fontWeight: 700, color: "#0b756c", borderBottom: "2px solid #e0e7ef" }}>
                          Status
                        </th>
                        <th style={{ textAlign: "center", padding: "10px 14px", fontSize: 12, fontWeight: 700, color: "#0b756c", borderBottom: "2px solid #e0e7ef" }}>
                          Last Updated
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgs.map((org) => {
                        const chipStyle = statusChipStyles[org.membership_status] || statusChipStyles["Not-Requested"];
                        const lastHistory = org?.status_history?.[org.status_history.length - 1];
                        return (
                          <tr
                            key={org.id}
                            style={{
                              borderBottom: "1px solid #f0f0f0",
                              borderLeft: `3px solid ${chipStyle.border}`,
                            }}
                          >
                            <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 500, color: "#1f2328" }}>
                              {orgIdToName[org.id] || org.id}
                            </td>
                            <td style={{ padding: "10px 14px", textAlign: "center" }}>
                              <span
                                style={{
                                  background: chipStyle.bg,
                                  color: chipStyle.color,
                                  padding: "3px 12px",
                                  borderRadius: 999,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  letterSpacing: 0.2,
                                }}
                              >
                                {org.membership_status || "Unknown"}
                              </span>
                            </td>
                            <td style={{ padding: "10px 14px", textAlign: "center", fontSize: 12, color: "#667085" }}>
                              {lastHistory?.changed_date
                                ? new Date(lastHistory.changed_date).toLocaleDateString()
                                : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailsModal;
